<!--
  DAVMAN AI INTEGRATION
  Este archivo es parte de la integración personalizada de IA para Davman Persa.
  NO es parte del código original de Chatwoot.

  Panel principal de configuración de Davman AI
-->
<script setup>
import { ref, onMounted, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import { checkHealth, getDashboard } from 'dashboard/api/davmanAi';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';

// Tabs
import PersonalityTab from './tabs/PersonalityTab.vue';
import FaqsTab from './tabs/FaqsTab.vue';
import SettingsTab from './tabs/SettingsTab.vue';
import StatsTab from './tabs/StatsTab.vue';

const loading = ref(true);
const serviceStatus = ref('checking');
const dashboardData = ref(null);
const activeTab = ref('personality');

const tabs = [
  { id: 'personality', label: 'Personalidad', icon: 'i-lucide-user' },
  { id: 'faqs', label: 'FAQs', icon: 'i-lucide-message-circle-question' },
  { id: 'settings', label: 'Configuración', icon: 'i-lucide-settings' },
  { id: 'stats', label: 'Estadísticas', icon: 'i-lucide-chart-bar' },
];

const statusColor = computed(() => {
  switch (serviceStatus.value) {
    case 'online':
      return 'text-green-500';
    case 'offline':
      return 'text-red-500';
    default:
      return 'text-yellow-500';
  }
});

const statusText = computed(() => {
  switch (serviceStatus.value) {
    case 'online':
      return 'Servicio activo';
    case 'offline':
      return 'Servicio desconectado';
    default:
      return 'Verificando...';
  }
});

const checkServiceStatus = async () => {
  try {
    const isHealthy = await checkHealth();
    serviceStatus.value = isHealthy ? 'online' : 'offline';
  } catch {
    serviceStatus.value = 'offline';
  }
};

const loadDashboard = async () => {
  try {
    dashboardData.value = await getDashboard();
  } catch (error) {
    console.error('[Davman AI] Error loading dashboard:', error);
  }
};

const refreshData = async () => {
  loading.value = true;
  await Promise.all([checkServiceStatus(), loadDashboard()]);
  loading.value = false;
};

onMounted(async () => {
  await refreshData();
});
</script>

<template>
  <SettingsLayout :is-loading="loading" loading-message="Cargando configuración...">
    <template #header>
      <BaseSettingsHeader
        title="Asistente IA Davman"
        description="Configura la personalidad, respuestas automáticas y comportamiento del asistente de inteligencia artificial."
      >
        <template #actions>
          <div class="flex items-center gap-4">
            <!-- Status indicator -->
            <div class="flex items-center gap-2 text-sm">
              <span
                class="size-2 rounded-full"
                :class="{
                  'bg-green-500': serviceStatus === 'online',
                  'bg-red-500': serviceStatus === 'offline',
                  'bg-yellow-500 animate-pulse': serviceStatus === 'checking',
                }"
              />
              <span :class="statusColor">{{ statusText }}</span>
            </div>
            <Button
              icon="i-lucide-refresh-cw"
              label="Actualizar"
              color="slate"
              faded
              @click="refreshData"
            />
          </div>
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="flex flex-col gap-6">
        <!-- Stats summary -->
        <div
          v-if="dashboardData"
          class="grid grid-cols-2 gap-4 p-4 rounded-xl md:grid-cols-4 bg-n-solid-3"
        >
          <div class="text-center">
            <div class="text-2xl font-semibold text-n-slate-12">
              {{ dashboardData.stats?.messagesProcessed || 0 }}
            </div>
            <div class="text-sm text-n-slate-11">Mensajes procesados</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-semibold text-n-slate-12">
              {{ dashboardData.stats?.suggestionsGenerated || 0 }}
            </div>
            <div class="text-sm text-n-slate-11">Sugerencias generadas</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-semibold text-n-slate-12">
              {{ dashboardData.stats?.suggestionsUsed || 0 }}
            </div>
            <div class="text-sm text-n-slate-11">Sugerencias usadas</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-semibold text-n-slate-12">
              {{ dashboardData.config?.faqsCount || 0 }}
            </div>
            <div class="text-sm text-n-slate-11">FAQs configuradas</div>
          </div>
        </div>

        <!-- Tabs -->
        <div class="border-b border-n-weak">
          <nav class="flex gap-1 -mb-px">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              class="flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-b-2"
              :class="
                activeTab === tab.id
                  ? 'border-[#87785B] text-[#87785B]'
                  : 'border-transparent text-n-slate-11 hover:text-n-slate-12 hover:border-n-weak'
              "
              @click="activeTab = tab.id"
            >
              <span :class="tab.icon" class="size-4" />
              {{ tab.label }}
            </button>
          </nav>
        </div>

        <!-- Tab content -->
        <div class="py-4">
          <PersonalityTab v-if="activeTab === 'personality'" />
          <FaqsTab v-else-if="activeTab === 'faqs'" @updated="loadDashboard" />
          <SettingsTab v-else-if="activeTab === 'settings'" />
          <StatsTab v-else-if="activeTab === 'stats'" :dashboard-data="dashboardData" />
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
